import {
  buildCoachAvailabilityQuery,
  CronofyDateTimePicker,
} from '@efdigital/ll-addon-coaching';
import {
  formatDate,
  formatTimeRange,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import type { CoachingSession } from '@efdigital/ll-addon-data';
import type AdminSessionsService from '@efdigital/ll-admin-v2/services/admin-sessions';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { AdminActionModal } from './admin-action-modal.gts';
import { AdminCronofyElementTokenProvider } from './admin-cronofy-element-token-provider.gts';

interface RescheduleValue {
  startTime: string;
  endTime: string;
}

interface SessionRescheduleModalSignature {
  Args: {
    coachingSession: CoachingSession;
    onSuccess?: () => Promise<void>;
  };
  Blocks: {
    reschedule: [showModal: () => void];
  };
}

const EMPTY_VALUE: RescheduleValue = { startTime: '', endTime: '' };

function toDate(date: string): Date {
  return new Date(date);
}

// Matches the coachee booking flow in ll-xceed-v2 — surfaces Cronofy Elements'
// own diagnostics in the console, which is otherwise the only signal when the
// picker mounts but never emits a slot.
const PICKER_CONFIG = { logs: 'info' } as const;

/**
 * The slice of Cronofy's DateTimePicker callback argument we use. Mirrors
 * `DateTimePickerAction` from `cronofy-elements`, which isn't a direct
 * dependency here and isn't re-exported by ll-addon-coaching — note `slot` is
 * nested inside `notification`, which is easy to get wrong (see onSlotSelected).
 */
interface DateTimePickerAction {
  notification: {
    type: string;
    /** Only present on `slot_selected`. */
    slot?: { start: string; end: string };
  };
}

export class SessionRescheduleModal extends Component<SessionRescheduleModalSignature> {
  @service declare adminSessions: AdminSessionsService;

  /** The new time keeps the session's current length — only the slot moves. */
  get durationMinutes(): number {
    const start = new Date(this.args.coachingSession.startTime).getTime();
    const end = new Date(this.args.coachingSession.endTime).getTime();
    return Math.max(1, Math.round((end - start) / 60_000));
  }

  reschedule = async (value: RescheduleValue) => {
    await this.adminSessions.rescheduleSession(this.args.coachingSession.id!, {
      startTime: value.startTime,
      endTime: value.endTime,
    });
    await this.args.onSuccess?.();
  };

  buildAvailabilityQuery = (
    coachSub: string | undefined
  ): ReturnType<typeof buildCoachAvailabilityQuery> => {
    // Only ever undefined before AdminCronofyElementTokenProvider resolves,
    // and it gates rendering this block until accountSub is known.
    if (!coachSub) {
      throw new Error('Cronofy account sub not resolved yet');
    }
    return buildCoachAvailabilityQuery({
      durationMinutes: this.durationMinutes,
      coachSub,
    });
  };

  // Confirming in the picker *is* the confirmation step, so it reschedules
  // straight away rather than arming a second button — same as the coachee
  // booking flow. Note `slot` sits inside `notification`, not alongside it.
  onSlotConfirmed = async (
    submitValue: (value: RescheduleValue) => Promise<void>,
    action: DateTimePickerAction
  ) => {
    const { type, slot } = action.notification;
    if (type === 'slot_selected' && slot) {
      await submitValue({ startTime: slot.start, endTime: slot.end });
    }
  };

  <template>
    <AdminActionModal
      @title="Reschedule session"
      @submitLabel="Reschedule session"
      @submittingLabel="Rescheduling…"
      @submitButtonClass="btn-primary"
      @initialValue={{EMPTY_VALUE}}
      @hideSubmitButton={{true}}
      @onSubmit={{this.reschedule}}
    >
      <:trigger as |showModal|>
        {{yield showModal to="reschedule"}}
      </:trigger>
      <:description>
        Support/ops reschedule - moves this session to a new time the coach is
        actually free for (same length as today), rebuilds their Cronofy
        calendar event, and re-registers the reminder and expiry schedules.
        Quota is adjusted the same way a late cancel-and-rebook would be if the
        new time falls inside the cancellation window, and
        <strong>both the coach and the coachee</strong>
        are notified by email. Confirming a slot below reschedules straight
        away.
      </:description>
      <:fields as |value setValue isSubmitting submitValue|>
        <div class="mb-3" data-test-reschedule-current-slot>
          <div class="form-label text-body-secondary mb-0">Currently scheduled
            for</div>
          <div class="d-flex flex-row column-gap-3 row-gap-1 flex-wrap">
            <span>{{formatDate (toDate @coachingSession.startTime)}}</span>
            <span>
              {{formatTimeRange
                (toDate @coachingSession.startTime)
                (toDate @coachingSession.endTime)
              }}
            </span>
          </div>
        </div>

        <AdminCronofyElementTokenProvider
          @coachId={{@coachingSession.coachUserId}}
          as |cronofyContext|
        >
          <CronofyDateTimePicker
            @cronofyContext={{cronofyContext}}
            @availabilityQuery={{this.buildAvailabilityQuery
              cronofyContext.accountStatus.accountSub
            }}
            @onAction={{fn this.onSlotConfirmed submitValue}}
            @elementConfig={{PICKER_CONFIG}}
            data-test-reschedule-picker
          />
        </AdminCronofyElementTokenProvider>
        {{#if isSubmitting}}
          <p
            class="form-text mt-2"
            aria-live="polite"
            data-test-reschedule-submitting
          >
            Rescheduling to
            {{formatDate (toDate value.startTime)}}
            at
            {{formatTimeRange (toDate value.startTime) (toDate value.endTime)}}…
          </p>
        {{/if}}
      </:fields>
    </AdminActionModal>
  </template>
}
